/**
 * Central lifecycle classification for Phase 4 documents.
 *
 * CommonJS source of truth so the trusted backend can apply the same policy
 * without duplicating retention intervals. The client re-exports this module
 * from lifecycle.js. Functions resolve this file from the repo locally and
 * copy it into the Functions package on deploy.
 *
 * Current class timing is the YouTube-era policy. Future provider adapters
 * must declare their own source policy rather than silently inheriting
 * YouTube rights.
 *
 * YouTube ordinary stored resource metadata (authorized OAuth observations
 * and public API-key observations) must be deleted or refreshed within 30
 * calendar days. Rhythmé's operational refresh target is 28 days so the
 * scheduled refresh path can succeed before that ceiling. Explicit OAuth
 * revocation / provider disconnect still deletes user-authorized provider
 * data promptly; the 30-day expiry is a backstop, not a substitute.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Operational refresh target — safety margin before the 30-day ceiling. */
const YOUTUBE_METADATA_REFRESH_MS = 28 * DAY_MS;

/** Hard maximum / expiry backstop required for stored YouTube API metadata. */
const YOUTUBE_METADATA_HARD_EXPIRY_MS = 30 * DAY_MS;

/**
 * Backward-compatible alias of the YouTube hard-expiry ceiling.
 * New code should use YOUTUBE_METADATA_HARD_EXPIRY_MS or retentionPolicyFor().
 */
const USER_AUTHORIZED_PROVIDER_REFRESH_MS = YOUTUBE_METADATA_HARD_EXPIRY_MS;

/** Closed catalogReviewTask retention. Writers must use retentionPolicyFor(). */
const CATALOG_REVIEW_CLOSED_RETENTION_MS = 90 * DAY_MS;

const DATA_CLASSES = Object.freeze({
  USER_AUTHORIZED_PROVIDER: 'user_authorized_provider',
  PUBLIC_PROVIDER_METADATA: 'public_provider_metadata',
  RHYTHME_CANONICAL: 'rhythme_canonical',
  OPEN_LICENSED_EXTERNAL: 'open_licensed_external',
  WIKIDATA_EXTERNAL: 'wikidata_external',
  USER_GENERATED: 'user_generated',
  CATALOG_ENRICHMENT_JOB: 'catalog_enrichment_job',
  CATALOG_ENRICHMENT_RUN: 'catalog_enrichment_run',
  CATALOG_OPERATIONAL: 'catalog_operational',
  UNKNOWN: 'unknown',
});

const POLICIES = Object.freeze({
  [DATA_CLASSES.USER_AUTHORIZED_PROVIDER]: Object.freeze({
    refreshable: true,
    refreshIntervalMs: YOUTUBE_METADATA_REFRESH_MS,
    hardExpiryMs: YOUTUBE_METADATA_HARD_EXPIRY_MS,
    timingSource: 'youtube_api_services_policy',
    deleteOnProviderDisconnect: true,
    deleteOnAccountDelete: true,
    ttlField: 'lifecycle.expiresAt',
  }),
  [DATA_CLASSES.PUBLIC_PROVIDER_METADATA]: Object.freeze({
    refreshable: true,
    refreshIntervalMs: YOUTUBE_METADATA_REFRESH_MS,
    hardExpiryMs: YOUTUBE_METADATA_HARD_EXPIRY_MS,
    timingSource: 'youtube_api_services_policy',
    deleteOnProviderDisconnect: false,
    deleteOnAccountDelete: false,
    ttlField: 'lifecycle.expiresAt',
  }),
  [DATA_CLASSES.RHYTHME_CANONICAL]: Object.freeze({
    refreshable: false,
    refreshIntervalMs: null,
    hardExpiryMs: null,
    timingSource: null,
    deleteOnProviderDisconnect: false,
    deleteOnAccountDelete: false,
    ttlField: null,
  }),
  [DATA_CLASSES.OPEN_LICENSED_EXTERNAL]: Object.freeze({
    refreshable: false,
    refreshIntervalMs: null,
    hardExpiryMs: null,
    timingSource: null,
    deleteOnProviderDisconnect: false,
    deleteOnAccountDelete: false,
    ttlField: null,
  }),
  [DATA_CLASSES.WIKIDATA_EXTERNAL]: Object.freeze({
    refreshable: false,
    refreshIntervalMs: null,
    hardExpiryMs: null,
    timingSource: null,
    deleteOnProviderDisconnect: false,
    deleteOnAccountDelete: false,
    ttlField: null,
  }),
  [DATA_CLASSES.USER_GENERATED]: Object.freeze({
    refreshable: false,
    refreshIntervalMs: null,
    hardExpiryMs: null,
    timingSource: null,
    deleteOnProviderDisconnect: false,
    deleteOnAccountDelete: true,
    ttlField: null,
  }),
  [DATA_CLASSES.CATALOG_ENRICHMENT_JOB]: Object.freeze({
    refreshable: false,
    refreshIntervalMs: null,
    hardExpiryMs: 7 * DAY_MS,
    timingSource: 'rhythme_operational_policy',
    deleteOnProviderDisconnect: false,
    deleteOnAccountDelete: false,
    ttlField: 'lifecycle.expiresAt',
  }),
  [DATA_CLASSES.CATALOG_ENRICHMENT_RUN]: Object.freeze({
    refreshable: false,
    refreshIntervalMs: null,
    hardExpiryMs: 30 * DAY_MS,
    timingSource: 'rhythme_operational_policy',
    deleteOnProviderDisconnect: false,
    deleteOnAccountDelete: false,
    ttlField: 'lifecycle.expiresAt',
  }),
  [DATA_CLASSES.CATALOG_OPERATIONAL]: Object.freeze({
    refreshable: false,
    refreshIntervalMs: null,
    hardExpiryMs: CATALOG_REVIEW_CLOSED_RETENTION_MS,
    timingSource: 'rhythme_operational_policy',
    deleteOnProviderDisconnect: false,
    deleteOnAccountDelete: false,
    ttlField: 'lifecycle.expiresAt',
  }),
  [DATA_CLASSES.UNKNOWN]: Object.freeze({
    refreshable: true,
    refreshIntervalMs: null,
    hardExpiryMs: null,
    timingSource: 'external_configuration',
    deleteOnProviderDisconnect: true,
    deleteOnAccountDelete: true,
    ttlField: 'lifecycle.expiresAt',
  }),
});

function requireNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a nonempty string`);
  }
  return value;
}

function timestampMs(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getTime();
  }
  if (value && typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  return null;
}

function offsetDate(baseMs, intervalMs) {
  if (baseMs == null || !Number.isSafeInteger(intervalMs)) {
    return null;
  }
  return new Date(baseMs + intervalMs);
}

function normalizeDataClass(value) {
  return Object.values(DATA_CLASSES).includes(value)
    ? value
    : DATA_CLASSES.UNKNOWN;
}

function retentionPolicyFor(dataClass) {
  const value = requireNonEmptyString(dataClass, 'dataClass');
  const policy = POLICIES[value];
  if (!policy) {
    throw new RangeError(`Unsupported lifecycle dataClass: ${value}`);
  }
  return policy;
}

function expiresAtFromPolicy(dataClass, fromTimestamp) {
  const policy = retentionPolicyFor(dataClass);
  return offsetDate(timestampMs(fromTimestamp), policy.hardExpiryMs);
}

function operationalLifecycle(dataClass, { expiresAt = null } = {}) {
  const classification = requireNonEmptyString(dataClass, 'dataClass');
  if (!POLICIES[classification] || classification === DATA_CLASSES.UNKNOWN) {
    throw new RangeError(`Unsupported lifecycle dataClass: ${classification}`);
  }
  return {
    dataClass: classification,
    expiresAt,
  };
}

function buildLifecycle({
  dataClass,
  obtainedVia,
  grantScope,
  lastFetchedAt = null,
  lastRefreshedAt = null,
  refreshDueAt,
  expiresAt,
} = {}) {
  const classification = requireNonEmptyString(dataClass, 'dataClass');
  if (
    classification === DATA_CLASSES.UNKNOWN ||
    !POLICIES[classification]
  ) {
    throw new RangeError(`Unsupported lifecycle dataClass: ${classification}`);
  }

  const policy = POLICIES[classification];
  const baseMs = timestampMs(lastRefreshedAt) ?? timestampMs(lastFetchedAt);

  const lifecycle = {
    dataClass: classification,
    obtainedVia: requireNonEmptyString(obtainedVia, 'obtainedVia'),
    lastFetchedAt,
    lastRefreshedAt,
    refreshDueAt: refreshDueAt !== undefined
      ? refreshDueAt
      : offsetDate(baseMs, policy.refreshIntervalMs),
    expiresAt: expiresAt !== undefined
      ? expiresAt
      : offsetDate(baseMs, policy.hardExpiryMs),
  };

  if (grantScope !== undefined && grantScope !== null) {
    lifecycle.grantScope = requireNonEmptyString(grantScope, 'grantScope');
  }

  return lifecycle;
}

module.exports = {
  DAY_MS,
  YOUTUBE_METADATA_REFRESH_MS,
  YOUTUBE_METADATA_HARD_EXPIRY_MS,
  USER_AUTHORIZED_PROVIDER_REFRESH_MS,
  CATALOG_REVIEW_CLOSED_RETENTION_MS,
  DATA_CLASSES,
  normalizeDataClass,
  retentionPolicyFor,
  expiresAtFromPolicy,
  operationalLifecycle,
  buildLifecycle,
};
